import { useCallback, useEffect, useMemo, useRef } from "react";
import type { LeafletMouseEvent } from "leaflet";
import L from "leaflet";
import type { Feature, Polygon } from "geojson";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMachine } from "@xstate/react";
import { xor, compact } from "lodash-es";
import { useQueryClient } from "react-query";
import { Box, Fab, Portal, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PolylineIcon from "@mui/icons-material/Polyline";
import { isHotkeyPressed } from "react-hotkeys-hook";
import { segmentsSelectionMachine } from "src/machines/segments-selection.machine";
import { useSegments } from "src/hooks/segments/useSegments";
import { useScenario } from "src/hooks/useScenario";
import { useSegmentDraw } from "src/hooks/useSegmentDraw";
import { SegmentsOnMap } from "src/components/segments/SegmentsOnMap";
import { EmissionsPage } from "src/pages/Case/EmissionsPage";
import { isReadOnly } from "src/utils/permission.utils";
import { findIntersectingSegments } from "src/utils/geometry.utils";
import type { HistoryState } from "src/types";
import { useTranslation } from "react-i18next";
import { Z_INDEX } from "src/config/zIndex";

interface Props {
  map: L.Map;
  controlContainer: Element | null;
}

export function TrafficEmissionPage(props: Props) {
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams<{
    caseId: string;
    scenarioId: string;
    emissionId: string;
    segmentId?: string;
  }>();

  const [state, send] = useMachine(segmentsSelectionMachine, {
    actions: {
      createNewSegment: (_content, event) => {
        navigate(`${trafficEmissionPath}/segments/new`, { state: { drawing: event.drawing } });
      },
      selectNew: (context, event) => {
        const selection = event.payload;
        routeToSegmentEdit(selection);

        // return { ...context, selection: event.payload };
      },
      selectAdd: (context, event) => {
        const currentSelection = currentSegmentSelection(location.state, params);
        const selection = [...currentSelection, ...event.payload];
        routeToSegmentEdit(selection);
      },
      selectAddXor: (context, event) => {
        const currentSelection = currentSegmentSelection(location.state, params);
        const selection = xor(currentSelection, event.payload);
        routeToSegmentEdit(selection);
      },
    },
  });
  const { data: segments } = useSegments(params.scenarioId || "");
  const { data: scenario } = useScenario(params.scenarioId || "");

  const { startDrawing, stopDrawing } = useSegmentDraw(props.map, {
    onDraw: (drawing) => send({ type: "CREATE_SEGMENT", drawing }),
  });

  const selectionPreviewRef = useRef<L.Polygon | null>(null);

  const startPolygonDrawing = useCallback(() => {
    const paneKey = "polygon-selection";
    let pane = props.map.getPane(paneKey);
    if (!pane) pane = props.map.createPane(paneKey);
    pane.style.zIndex = String(Z_INDEX.selectionPreview);
    pane.style.display = "";

    props.map.once("pm:drawstart", (e) => {
      const wl = e.workingLayer as L.Polyline;
      const handleMouseMove = (mouseEvent: L.LeafletMouseEvent) => {
        const raw = wl.getLatLngs();
        const confirmed = raw[0] instanceof L.LatLng ? (raw as L.LatLng[]) : (raw as L.LatLng[][])[0] ?? [];
        const latlngs = [...confirmed, mouseEvent.latlng];
        if (latlngs.length >= 3) {
          if (!selectionPreviewRef.current) {
            selectionPreviewRef.current = L.polygon(latlngs, {
              color: "#ff6347",
              fillColor: "#ff6347",
              fillOpacity: 0.2,
              weight: 1,
              dashArray: "4 4",
              interactive: false,
              pane: paneKey,
            }).addTo(props.map);
          } else {
            selectionPreviewRef.current.setLatLngs(latlngs);
            selectionPreviewRef.current.redraw();
          }
        }
      };

      props.map.on("mousemove", handleMouseMove);
      props.map.once("pm:drawend", () => {
        props.map.off("mousemove", handleMouseMove);
        selectionPreviewRef.current?.remove();
        selectionPreviewRef.current = null;
      });
    });

    props.map.pm.setGlobalOptions({ panes: { layerPane: paneKey, vertexPane: paneKey } });
    props.map.pm.enableDraw("Polygon", {
      snappable: false,
      finishOn: "dblclick",
      templineStyle: {
        color: "#ff6347",
      },

      hintlineStyle: {
        color: "#ff6347",
        dashArray: "5, 5",
      },

      pathOptions: {
        color: "#ff6347",
      },
    });

    props.map.on("pm:create", (event) => {
      const { layer } = event;

      // @ts-ignore - toGeoJSON exists but typing is incomplete
      const drawnPolygon = layer.toGeoJSON() as Feature<Polygon>;
      const intersectingIds = findIntersectingSegments(drawnPolygon, segments || []);

      layer.remove();

      if (intersectingIds.length > 0) {
        setTimeout(() => {
          if (isHotkeyPressed(navigator.userAgent.includes("Mac") ? "meta" : "ctrl")) {
            send({ type: "SELECT_ADD", payload: intersectingIds });
          } else {
            send({ type: "SELECT_NEW", payload: intersectingIds });
          }
        }, 1);
      } else {
        send({ type: "CANCEL" });
      }
    });
  }, [props.map, segments, send]);

  const stopPolygonDrawing = useCallback(() => {
    props.map.pm.disableDraw("Polygon");
    props.map.off("pm:create");
    props.map.pm.setGlobalOptions({ panes: { layerPane: "overlayPane" } });

    const pane = props.map.getPane("polygon-selection");
    if (pane) pane.style.display = "none";
  }, [props.map]);

  const trafficEmissionPath = useMemo(
    () => `/cases/${params.caseId}/scenarios/${params.scenarioId}/emissions/traffic/${params.emissionId}`,
    [params.caseId, params.emissionId, params.scenarioId]
  );

  const routeToSegmentEdit = useCallback(
    (selection: number[]) => {
      if (selection.length === 1) {
        // route to single edit form
        navigate(`${trafficEmissionPath}/segments/${selection[0]}`);
      } else if (selection.length > 1) {
        // route to bulk edit form
        navigate(`${trafficEmissionPath}/segments/bulk`, { state: { bulkEdit: selection } });
      }
    },
    [navigate, trafficEmissionPath]
  );

  const currentSelection = useMemo(() => {
    return currentSegmentSelection(location.state, params);
  }, [location.state, params]);

  // TODO: refactor this effect into a seperate hook/function? Think about it!
  useEffect(() => {
    if (state.matches("draw")) {
      // do nothing
      // return empty cleanup
      return () => {};
    }

    if (!state.matches("select_with_polygon")) {
      props.map.once("click", (event: LeafletMouseEvent) => {
        navigate(trafficEmissionPath);
        send({ type: "CANCEL" });
      });
      // cleanup once event handler
      return function cleanUp() {
        props.map.off("click");
      };
    }
  }, [props.map, send, state, trafficEmissionPath, navigate]);

  useEffect(() => {
    // TODO: rerenderWMS is outdated, segments are now rendered via geojson, thus update naming
    if (location.state?.action === "rerenderWMS") {
      queryClient.invalidateQueries("segments");
    }
  }, [location, queryClient]);

  useEffect(() => {
    if (!state.matches("select_with_polygon")) {
      stopPolygonDrawing();
    }
  }, [state, stopPolygonDrawing]);

  useEffect(() => {
    if (!state.matches("draw") && !state.matches("select_with_polygon")) {
      stopDrawing();
    }
  }, [state, stopDrawing]);

  function handleDrawToggle() {
    if (state.matches("draw")) return send({ type: "CANCEL" });
    send({ type: "DRAW_SEGMENT" });
    queueMicrotask(startDrawing);
  }

  function handlePolygonToggle() {
    if (state.matches("select_with_polygon")) {
      send({ type: "CANCEL" });
      return;
    }
    send({ type: "POLYGON_SELECT" });
    queueMicrotask(startPolygonDrawing);
  }

  return render();

  function render() {
    if (!segments || !scenario) return null;

    return (
      <>
        <EmissionsPage map={props.map} />
        <SegmentsOnMap
          map={props.map}
          segments={segments}
          selected={currentSelection}
          disableClicks={
            state.matches("draw") || state.matches("select_with_lasso") || state.matches("select_with_polygon")
          }
          onSelect={(segment, event) => {
            const addToSelection = event.originalEvent.metaKey || event.originalEvent.ctrlKey;

            if (addToSelection) {
              send({ type: "SELECT_ADD_XOR", payload: [segment.segmentId] });
            } else {
              send({ type: "SELECT_NEW", payload: [segment.segmentId] });
            }
          }}
        />

        <Portal container={props.controlContainer}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Fab
              size="small"
              onClick={(event) => handleDrawToggle()}
              color={state.matches("draw") ? "primary" : "default"}
              disabled={isReadOnly(scenario.permissions.updateEmission)}
            >
              <AddIcon fontSize="small" />
            </Fab>
            <Tooltip
              title={t("label.drawPolygonToSelect", {
                modifier: navigator.userAgent.includes("Mac") ? "⌘ cmd" : "ctrl",
              })}
              componentsProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
            >
              <Fab
                size="small"
                onClick={(event) => handlePolygonToggle()}
                color={state.matches("select_with_polygon") ? "primary" : "default"}
              >
                <PolylineIcon fontSize="small" />
              </Fab>
            </Tooltip>
          </Box>
        </Portal>
      </>
    );
  }
}

function currentSegmentSelection(
  locationState: HistoryState,
  params: Partial<{ segmentId: string | undefined }>
): number[] {
  const selectionInLocationState = (locationState?.bulkEdit || []).map((id) => +id);
  const selectionInParams = params.segmentId && params.segmentId !== "bulk" ? +params.segmentId : null;
  return compact([...selectionInLocationState, selectionInParams]);
}
